import React, { useEffect, useState, useRef } from 'react';
import { TerminalProps } from "./Terminal";
import Modal from 'react-bootstrap/Modal';
import { InventoryHostsWidget, targetRefsToParam } from './PolicyEditor';
import { TerminalWorkspace } from "./TerminalWorkspace";
import type { TerminalWorkspaceHandle } from "./TerminalWorkspace";
import { Modal as RdModal } from './components/utils/modal';
import type { TargetRef } from './components/ExecutionTargetSelect';

declare global {
    interface Window { [key: string]: any }
}

interface ModalExecutorProps {
    terminal: TerminalProps,
    terminalRef: React.RefObject<TerminalWorkspaceHandle>,
    title: string,
    onClose: () => void,
    onAbort: () => void,
    activeJobData?: any
}

export function ModalExecutor({ terminal, title, onClose, onAbort, terminalRef, activeJobData }: ModalExecutorProps) {
    return (
        <RdModal
            show={true}
            size="xl"
            onClose={onClose}
            backdrop="static"
            title={title}
            footer={
                <>
                    <button
                        className="rd-btn rd-btn--warning rd-btn--large"
                        style={{ flex: 1 }}
                        onClick={onAbort}
                    >
                        Приостановить выполнение
                    </button>
                    <button
                        className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--large"
                        style={{ flex: 1 }}
                        onClick={onClose}
                    >
                        Закрыть
                    </button>
                </>
            }
        >
            <TerminalWorkspace
                id={terminal.instance}
                ref={terminalRef}
                executeParams={terminal.params}
                terminalProps={{ onFinish: terminal.onFinish, "instance": terminal.instance }}
                terminalHeight="calc(100vh - 210px)"
                activeJobData={activeJobData}
            />
        </RdModal>
    );
}

export interface ExecutorCommand {
    params?: any,
    modal?: string,
    onClose?: () => void,
    onFinish?: (text: string) => void,
    onAbort?: () => void,
    needsApiCallToCancel?: boolean,
    useFormInput?: boolean,
    apiGetChunkUrl?: string,
    activeJobData?: any
}

export interface ExecutorProps {
    instance?: string,
    height?: string,
}

function ModalExecutorWithForm({ command, instance, terminalRef, onAbort, onClose, height }: {
    command: ExecutorCommand,
    instance: string,
    terminalRef: React.RefObject<TerminalWorkspaceHandle>,
    onAbort: () => void,
    onClose: () => void,
    height?: string,
}) {
    const [testHosts, setTestHosts] = useState<TargetRef[]>([]);
    const [verbose] = useState<boolean>(false);
    const setVerbose = () => { };

    const onTest = () => {
        if (testHosts.length === 0) return;
        const host = targetRefsToParam(testHosts);
        const updatedParams = { ...command.params, host, verbose };
        terminalRef.current?.getExecuteTab()?.execute({
            instance: instance,
            params: updatedParams,
        });
    };

    const handleAbort = () => {
        terminalRef.current?.getExecuteTab()?.abort(true);
        onAbort();
    }

    return (
        <Modal show={true} size="xl" onHide={onClose} backdrop="static">
            <Modal.Header>
                <Modal.Title>{command.modal}</Modal.Title>
                <button type="button" className="close" onClick={onClose} aria-label="Close"></button>
            </Modal.Header>
            <Modal.Body>
                <TerminalWorkspace
                    ref={terminalRef}
                    id={instance}
                    terminalHeight={height}
                    terminalInstance={instance}
                />
                <InventoryHostsWidget
                    terminalRef={terminalRef}
                    testHosts={testHosts}
                    setTestHosts={setTestHosts}
                    verbose={verbose}
                    setVerbose={setVerbose}
                    onTest={onTest}
                    onAbort={handleAbort}
                    hideVerboseSwitch={true}
                    hideSaveWarning={true}
                />
            </Modal.Body>
            <Modal.Footer>
                <button className="rd-btn rd-btn--primary rd-btn--medium" onClick={onClose}>Закрыть</button>
            </Modal.Footer>
        </Modal>
    );
}

export function Executor(props: ExecutorProps) {
    const terminalRef = useRef<TerminalWorkspaceHandle>(null!);
    const [command, setCommand] = useState<ExecutorCommand>({});
    const instance = props.instance || "executor";

    const setCommandRef = useRef(setCommand);
    useEffect(() => { setCommandRef.current = setCommand; }, []);

    const handlerRef = useRef<((cmd: ExecutorCommand) => void) | null>(null);
    if (!handlerRef.current) {
        handlerRef.current = (cmd: ExecutorCommand) => {
            setCommandRef.current(cmd);
        };
    }

    const installedRef = useRef(false);
    if (!installedRef.current) {
        (window as any)[instance] = handlerRef.current;
        installedRef.current = true;
    }

    useEffect(() => {
        window.dispatchEvent(new CustomEvent(`executor:${instance}:ready`));
    }, [instance]);

    const onClose = () => {
        onAbort();
        setCommand({});
        command.onClose && command.onClose();
    };

    const onAbort = () => {
        terminalRef.current?.getExecuteTab()?.abort(command.needsApiCallToCancel);
        command.onAbort && command.onAbort();
    };

    const onFinish = (text: string) => {
        command.onFinish && command.onFinish(text);
    };

    if (command.params) {
        if (command.useFormInput) {
            return (
                <ModalExecutorWithForm
                    command={command}
                    instance={instance}
                    terminalRef={terminalRef}
                    onAbort={onAbort}
                    onClose={onClose}
                    height={props.height}
                />
            );
        }
        return (command.modal ?
            <ModalExecutor
                terminalRef={terminalRef}
                terminal={{ instance: instance, params: command.params, onFinish: onFinish } as TerminalProps}
                onAbort={onAbort}
                title={command.modal!}
                onClose={onClose}
                activeJobData={command.activeJobData}
            />
            :
            <TerminalWorkspace
                ref={terminalRef}
                id={instance}
                executeParams={command.params}
                apiExecuteUrl={command.apiGetChunkUrl}
                terminalProps={{ onFinish, instance: instance }}
                terminalHeight={props.height}
                activeJobData={command.activeJobData}
            />
        );
    } else {
        return (<div />);
    }
}

export default Executor;
